/**
 * LE DUE DECISIONI PURE SU UNA RIGA DI `GET /matches?include=waiting`.
 *
 * Vivono qui e non dentro il provider per una ragione sola: **un banco puo'
 * eseguirle**. `ResumableMatchContext.tsx` e' un componente React e un gate a
 * `node --experimental-strip-types` non lo importa; una regex sul suo sorgente
 * proverebbe che la riga E' SCRITTA, non che decida bene — ed e' esattamente la
 * distinzione che questo fronte ha gia' pagato una volta (diciassette asserzioni
 * verdi sulla forma del cablaggio, mentre il difetto era nella forma del DATO).
 *
 * ⚠️ LE DUE FORME DI RIGA, e non sono la stessa con un campo in piu'
 * [M `tma5` 2026-09-07, su un appuntamento VERO su staging]:
 *  · riga VIVA         → `white`/`black`, `ended`, `status:"active"` sotto l'opt-in;
 *  · riga APPUNTAMENTO → `players:{W,B}`, `expiresAt`, `createdBy`, e **niente
 *                        `ended`, niente `white`/`black`**.
 */

/** Un posto a sedere, come lo serve `identity()` del gameserver. */
export interface Seat {
  userId: string;
  displayName: string;
}

/** Una riga della lista, in una qualunque delle due forme. */
export interface MatchRow {
  matchId: string;
  /**
   * ⚠️ NON e' un'unione chiusa nella realta': il server ne aggiungera'
   * (`abandoned`, concordato con or49 il 07/09). Il tipo elenca cio' che questo
   * client sa DIRE; `isRowOpen` scarta il resto invece di indovinarlo.
   */
  status?: 'active' | 'waiting' | (string & {});
  ended?: boolean;
  white?: Seat;
  black?: Seat;
  players?: { W?: Seat; B?: Seat };
  /**
   * Solo sulla riga APPUNTAMENTO, e **stringa ISO o `null`**, non un numero.
   * [M or56 2026-09-07, `qgammon-gameserver/src/rest.ts:283` @ origin/main:
   * `expiresAt: s.expiresAt !== null ? new Date(s.expiresAt).toISOString() : null`]
   */
  expiresAt?: string | null;
  /** Chi ha coniato l'invito — `null` su una riga che precede la colonna. */
  createdBy?: string | null;
  /**
   * Perche' la riga e' finita. Esiste **solo** sulle righe senza vincitore
   * (`server_restart` · `never_started` · `never_played` · `expired` · `cancelled`),
   * quindi la sua PRESENZA e' gia' la risposta, e il valore serve solo alla frase.
   */
  abandonedReason?: string | null;
}

/**
 * I due posti, da qualunque delle due forme. `null` se la riga non ne porta
 * nessuna — e allora si SALTA la riga invece di lanciare: una forma che non
 * conosciamo non deve spegnere il recupero anche per le righe che conosciamo.
 */
export function seatsOf(row: MatchRow): { W: Seat; B: Seat } | null {
  if (row.players?.W && row.players?.B) return { W: row.players.W, B: row.players.B };
  if (row.white && row.black) return { W: row.white, B: row.black };
  return null;
}

/**
 * L'istante di scadenza in epoch ms, o `null` se non c'e' o non si legge.
 *
 * ⚠️ `null` significa **«non lo so»**, e non deve poter passare per «scaduto»:
 * una data illeggibile non e' una prova di scadenza.
 */
export function expiryMsOf(row: MatchRow): number | null {
  if (typeof row.expiresAt !== 'string') return null;
  const t = Date.parse(row.expiresAt);
  return Number.isFinite(t) ? t : null;
}

/**
 * La riga vale ancora la pena di mostrarla?
 *
 * DUE modi di essere finita, e il primo non copre il secondo:
 *  · `ended === true` — la partita e' conclusa. `=== true` e non `!row.ended`:
 *    una riga di appuntamento NON porta `ended`, e `undefined` significa «non
 *    finita», non «non lo so»;
 *  · **`expiresAt` passato — l'APPUNTAMENTO e' scaduto.** Senza questo la banda
 *    direbbe «hai un appuntamento» per un appuntamento morto: il filtro su
 *    `ended` non puo' vederlo, perche' quel campo li' non c'e'.
 *
 * ⚠️ La finestra e' piccola ed e' misurata: il gameserver spazza gli
 * appuntamenti scaduti ogni 60 s [M or56 2026-09-07, `game/reaper.ts:31`
 * `REAP_INTERVAL_MS = 60_000`], quindi una riga scaduta e' servibile per al piu'
 * una spazzata — **piu' tutto il tempo che la risposta resta ferma nel client,
 * che nessuno spazza.** Il filtro costa un confronto e copre entrambi.
 * ⛔ `> now` e non `>= now`: un appuntamento che scade in questo millisecondo e'
 * scaduto.
 */
export function isRowOpen(row: MatchRow, now: number): boolean {
  if (row.ended === true) return false;
  // 🔴 IL RAMO `else` CHE NON DEVE ESISTERE. Chi legge `status` con
  // «`waiting` ⇒ appuntamento, tutto il resto ⇒ partita viva» tratta come una
  // partita in corso OGNI stato che il server aggiungerà domani. E ne sta
  // aggiungendo uno: la DISDETTA (`status: "abandoned"` + `abandonedReason`,
  // concordata con or49 il 07/09) — che con quel ternario comparirebbe sulla
  // banda come **«Hai una partita in corso con X»**, cioe' la falsita' esatta
  // che tutto questo lavoro toglie, reintrodotta da un `else`.
  // ⇒ passano SOLO gli stati che questo client sa dire. Uno stato che non
  // conosciamo non si mostra: non e' un'omissione, e' il rifiuto di
  // pronunciare una frase che non abbiamo. Il giorno che la disdetta si
  // disegna, si aggiunge il suo ramo QUI e la riga torna visibile con la sua
  // frase — non prima.
  if (row.status !== undefined && row.status !== 'waiting' && row.status !== 'active') return false;
  // ⚠️ E il campo che esiste SOLO su una riga finita: se c'e', la riga e' finita —
  // qualunque cosa dica `status`.
  // [M or49, staging del 07/09 21:27:58Z: `?include=waiting` ora rende anche gli
  // appuntamenti chiusi da meno di 48 h, con `abandonedReason` `cancelled`/`expired`.]
  // 🔴 La riga qui sopra basterebbe SE la forma fosse quella dichiarata
  // (`status: "abandoned"`), e quella forma io **non l'ho misurata**: i due profili di
  // collaudo non hanno appuntamenti chiusi, e crearne uno significherebbe disdire
  // l'appuntamento vero che tutti stanno usando. ⇒ questa seconda condizione copre la
  // forma che non ho visto, e costa un confronto. *Una guardia scritta su una forma
  // trasportata deve reggere anche se la forma e' un'altra.*
  if (typeof row.abandonedReason === 'string' && row.abandonedReason.length > 0) return false;
  const t = expiryMsOf(row);
  return t === null || t > now;
}

/**
 * CHI HA COMINCIATO: `'me'` se l'appuntamento nasce da un mio invito che
 * qualcuno ha accettato, `'them'` se ho accettato io il suo, `'unknown'` se il
 * server non lo dice.
 *
 * 🔴 PERCHE' QUESTO E NON «CHI E' PRESENTE», che sarebbe la domanda naturale.
 * [M or56 2026-09-07, `qgammon-gameserver/src/game/payloads.ts:52` @ origin/main]
 * La riga di appuntamento espone i posti con `identity()` — `userId`,
 * `displayName`, `avatarUrl`, `isBot`, `elo?` — e **niente `connected`**, che
 * invece la riga VIVA porta. ⇒ **il client non puo' sapere chi dei due manca.**
 * [M `tma12`, 07/09, due profili sullo stesso appuntamento nello stesso istante:
 * *ciascuno dei due leggeva che l'altro lo stava aspettando*.] Finche' nessuno e'
 * entrato, «X ti sta aspettando» e' vero per entrambi e falso sul fatto: nessuno
 * aspetta nessuno, l'appuntamento e' soltanto aperto.
 * ⇒ Si dice cio' che si SA: chi ha invitato chi, che e' un fatto compiuto.
 *
 * ⛔ `createdBy` assente ⇒ `'unknown'`, mai un ripiego su `'them'`: una riga
 * vecchia non e' un invito ricevuto.
 */
export function initiatorOf(row: MatchRow, myId: string | null): 'me' | 'them' | 'unknown' {
  if (typeof row.createdBy !== 'string' || myId === null) return 'unknown';
  return row.createdBy === myId ? 'me' : 'them';
}

/**
 * Il tetto di `setTimeout`: oltre questo il ritardo va in overflow a 32 bit e il
 * timer **scatta subito**, ogni volta. Un valore di scadenza assurdo (una data
 * del 3000, un `expiresAt` in secondi letto come millisecondi) non produrrebbe
 * un errore: produrrebbe un ciclo caldo che riarma se stesso a ogni disegno.
 */
export const MAX_TIMER_MS = 2_147_483_647;

/**
 * FRA QUANTO GUARDARE DI NUOVO QUESTO APPUNTAMENTO — `null` se non c'e' niente
 * da guardare, `0` se e' GIA' scaduto, altrimenti i millisecondi che mancano.
 *
 * ═══ 🔴 PERCHE' ESISTE: la meta' di L-481 che `isRowOpen` non copre ═══
 * `isRowOpen` decide **all'istante della lettura**, e il provider la chiama una
 * volta sola: il suo effetto ha `[enabled, myId, activeMatchId]` fra le
 * dipendenze, e nessuna delle tre cambia col passare del tempo. ⇒ un
 * appuntamento **gia'** scaduto quando si apre l'app non compare — ed e'
 * esattamente cio' che il gate provava, con date vere e un `NOW` fisso — ma uno
 * che scade **mentre l'app e' aperta resta sulla banda**, col suo tocco che
 * porta a una partita morta. *Il filtro c'era; il momento in cui rifarlo no.*
 * ⚠️ La finestra non e' teorica: gli appuntamenti vivono minuti — quello vero del
 * 07/09 e' morto alle 20:06:39Z — e una TMA aperta resta aperta. E questo file
 * lo diceva gia', senza chiuderlo: «una riga scaduta e' servibile per al piu' una
 * spazzata, **piu' tutto il tempo che la risposta resta ferma nel client, che
 * nessuno spazza**».
 *
 * ⛔ IL RITORNO E' UN RITARDO, NON UN VERDETTO, e la differenza e' il tetto: se
 * la scadenza cade oltre `MAX_TIMER_MS` il ritardo viene **tosato**, quindi lo
 * scatto del timer NON significa «scaduto», significa «riguarda». Chi chiama
 * deve ri-chiamare questa funzione allo scatto invece di credergli — e' l'unico
 * modo perche' una data assurda costi un timer in piu' e non un ciclo caldo.
 * ⇒ **`0` e' l'unica risposta che vuol dire scaduto**, e si ottiene solo da un
 * confronto vero.
 */
export function expiryTimerDelay(expiresAt: number | null, now: number): number | null {
  if (expiresAt === null || !Number.isFinite(expiresAt)) return null;
  if (expiresAt <= now) return 0;
  return Math.min(expiresAt - now, MAX_TIMER_MS);
}
